import {
  Activity,
  FlaskConical,
  TrendingDown,
  CheckCircle,
  CircleDashed,
  CircleSlash,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../ui/utils';
import {
  scoreInputInventory,
  supportingInputs,
  contextInputs,
  findingLabel,
  trendLabel,
  type OrganScore,
  type InputInventoryEntry,
  type InputStatus,
  type SupportingInput,
} from './organHealth';

/** "N days ago" from an ISO/date string; '' when unparseable. */
function recency(date?: string): string {
  if (!date) return '';
  const t = Date.parse(date.replace(' ', 'T'));
  if (Number.isNaN(t)) return '';
  const days = Math.max(0, Math.round((Date.now() - t) / 86_400_000));
  if (days === 0) return 'today';
  if (days < 31) return `${days}d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.round(months / 12)}y ago`;
}

/** A labelled chip group (findings, trends) — icon + small heading + chips. */
function ChipGroup({
  icon: Icon,
  heading,
  items,
  tone,
}: {
  icon: typeof Activity;
  heading: string;
  items: string[];
  tone: 'attention' | 'neutral';
}) {
  if (items.length === 0) return null;
  const chip =
    tone === 'attention'
      ? 'border-(--status-attention)/30 bg-status-attention-bg text-status-attention-text'
      : 'border-border bg-surface-sunken text-text-secondary';
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] text-text-tertiary">
        <Icon className="size-3.5" aria-hidden="true" />
        {heading}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {items.map((label) => (
          <span key={label} className={`rounded-sm border px-2 py-0.5 text-[11px] font-medium ${chip}`}>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Each input's on-record status, encoded by glyph + word (never colour alone).
 * Neutral tones only — saturated colour is reserved for clinical severity. */
const STATUS_META: Record<InputStatus, { icon: LucideIcon; tone: string; word: string }> = {
  captured: { icon: CheckCircle, tone: 'text-text-secondary', word: 'captured' },
  scored_no_raw: { icon: CircleDashed, tone: 'text-text-tertiary', word: 'value not stored' },
  missing: { icon: CircleSlash, tone: 'text-text-tertiary', word: 'not on record' },
};

/** One input the score model consumes — its value (or absence), and where it
 * landed in the math. Present, partial, and missing inputs all render, so the
 * clinician sees the whole picture the score was (and wasn't) built from. */
function InventoryRow({ entry }: { entry: InputInventoryEntry }) {
  const meta = STATUS_META[entry.status];
  const Icon = meta.icon;
  const when = entry.status === 'captured' ? recency(entry.date) : '';
  return (
    <li className="flex items-start gap-2.5">
      <Icon className={cn('mt-[3px] size-3.5 shrink-0', meta.tone)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-text-primary">{entry.label}</span>
          {entry.status === 'captured' ? (
            <>
              <span className="text-sm tabular-nums text-text-primary">
                {entry.value}
                {entry.unit ? <span className="text-[11px] text-text-tertiary"> {entry.unit}</span> : null}
              </span>
              {when ? <span className="text-[10px] text-text-tertiary/80">{when}</span> : null}
            </>
          ) : (
            <span className="text-[11px] uppercase tracking-[0.06em] text-text-tertiary">{meta.word}</span>
          )}
          {entry.role === 'penalty' ? (
            <span className="rounded-sm border border-border-subtle px-1 py-px text-[9px] uppercase tracking-wide text-text-tertiary">
              penalty
            </span>
          ) : null}
          {/* A computed value (e.g. eGFR from creatinine via CKD-EPI 2021) is
              labelled so it never reads as a directly-measured result. */}
          {entry.derived ? (
            <span
              title={entry.derived}
              className="rounded-sm border border-border-subtle px-1 py-px text-[9px] uppercase tracking-wide text-text-tertiary"
            >
              derived
            </span>
          ) : null}
        </div>
        {entry.landed ? (
          <div className="text-[11px] text-text-tertiary">
            {entry.landed}
            {entry.status === 'scored_no_raw' ? ' · raw value recoverable on Rescore' : ''}
          </div>
        ) : null}
        {entry.consequence ? <div className="text-[11px] text-text-tertiary">{entry.consequence}</div> : null}
      </div>
    </li>
  );
}

/** A supporting value the scorer weighed beyond the scoring axes — a red-flag
 * check or the lung gate. Always on record (present-only), with what it feeds. */
function SupportingRow({ entry }: { entry: SupportingInput }) {
  const when = recency(entry.date);
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle className="mt-[3px] size-3.5 shrink-0 text-text-secondary" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-text-primary">{entry.label}</span>
          <span className="text-sm tabular-nums text-text-primary">
            {entry.value}
            {entry.unit ? <span className="text-[11px] text-text-tertiary"> {entry.unit}</span> : null}
          </span>
          {when ? <span className="text-[10px] text-text-tertiary/80">{when}</span> : null}
        </div>
        <div className="text-[11px] text-text-tertiary">{entry.role}</div>
      </div>
    </li>
  );
}

/**
 * Everything that fed this organ's score, in one place — the complete inventory
 * of inputs the score model consumes, each resolved to an honest status:
 *   - captured      — raw value + date persisted, shown with where it landed;
 *   - value-not-stored — it drove a sub-score/penalty but the raw value wasn't
 *     saved (older rows); a Rescore recovers it;
 *   - not on record — the scorer never guessed, so it lowered confidence, not
 *     the score (the exact consequence is spelled out);
 * plus the context flags (sex, dialysis), coded conditions, and acute trends
 * that shaped it. Never fabricated. Renders for any scored organ (so the card
 * never looks empty just because a value wasn't captured); hides only for the
 * Overall roll-up and organs with no signal at all (the "Needed to score" list
 * covers those).
 */
export function ClinicalInputs({ organ }: { organ: OrganScore }) {
  const inventory = scoreInputInventory(organ);
  const supporting = supportingInputs(organ);
  const context = contextInputs(organ);
  const findings = (organ.structured_fields?.findings_present ?? []).map(findingLabel);
  const trends = (organ.structured_fields?.trends_present ?? []).map(trendLabel);

  const hasSignal = inventory.some((e) => e.status !== 'missing') || supporting.length > 0;
  if (!hasSignal && findings.length === 0 && trends.length === 0 && context.length === 0) return null;

  const captured = inventory.filter((e) => e.status === 'captured').length;
  const total = inventory.length;
  const anyLegacy = inventory.some((e) => e.status === 'scored_no_raw');

  return (
    <div className="space-y-4 rounded border border-border-subtle bg-surface-sunken/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex items-center gap-2">
          <FlaskConical className="size-4 text-text-tertiary" aria-hidden="true" />
          <span className="text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
            Data behind this score
          </span>
        </div>
        {total > 0 ? (
          <span className="text-[11px] tabular-nums text-text-tertiary">
            {captured} of {total} scoring inputs captured
          </span>
        ) : null}
      </div>

      {context.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-text-tertiary">Context</span>
          {context.map((c) => (
            <span
              key={c}
              className="rounded-full border border-border-subtle bg-surface-sunken px-2 py-0.5 text-[11px] text-text-secondary"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {inventory.length > 0 && (
        <ul className="space-y-2.5">
          {inventory.map((entry) => (
            <InventoryRow key={entry.key} entry={entry} />
          ))}
        </ul>
      )}

      {supporting.length > 0 && (
        <div className="space-y-2.5 border-t border-border-subtle pt-3">
          <div className="text-[11px] text-text-tertiary">Also weighed — red-flag &amp; gate checks</div>
          <ul className="space-y-2.5">
            {supporting.map((entry) => (
              <SupportingRow key={entry.key} entry={entry} />
            ))}
          </ul>
        </div>
      )}

      {anyLegacy && (
        <p className="text-[11px] leading-relaxed text-text-tertiary">
          Some raw values weren't stored with this older score — their sub-scores are on record, and a
          Rescore recaptures the underlying labs with dates.
        </p>
      )}

      {(findings.length > 0 || trends.length > 0) && (
        <div className="space-y-3 border-t border-border-subtle pt-3">
          <ChipGroup icon={Activity} heading="Conditions present" items={findings} tone="attention" />
          <ChipGroup icon={TrendingDown} heading="Acute changes" items={trends} tone="attention" />
        </div>
      )}
    </div>
  );
}
